import React, { useState, useRef, useEffect } from 'react';
import Tab from './Tab';
import ContextMenu from '../Overlays/ContextMenu';
import { closeTab } from '../../helpers/tabHelpers';
import { renameItem } from '../../helpers/projectHelpers';
import { debug } from '../../helpers/debug';
import { createDoc, openDoc } from '../../helpers/docHelpers';
import { computeOpenBehavior } from '../../helpers/navHelpers';
import { screenSafePlacement } from '../../helpers/uiHelpers';
import { useViewMode } from '../../state/ViewModeContext';
import Icon from '../Icon';

export default function TabShelf({
  tabs,
  activeTabId,
  setActiveTabId,
  setTabs,
  setSelectedDocId,
  onToggleOptions,
  optionsOpen,
  itemsMap,
  setItemsMap,     // updated to match useDocsTree
  dragStart,
  dragEnd,
  dropInTabs,
  draggedId,
  splitScreen,
  setSplitScreen,
  leftPercent = 50,
  leftActiveTabId,
  setLeftActiveTabId,
  rightActiveTabId,
  setRightActiveTabId,
  activePane,
  setActivePane,
  currentProject,
  user,
}) {
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverPosition, setDragOverPosition] = useState(null); // 'before' | 'after'
  const [dragOverPane, setDragOverPane] = useState(null); // 'left' | 'right'
  const [tabMenuState, setTabMenuState] = useState(null);
  const tabsLeftRef = useRef(null);
  const tabsRightRef = useRef(null);
  const tabRefs = useRef({});
  const tabMenuTriggerRef = useRef(null);

  const { isEditable } = useViewMode();

  // Separate tabs by pane (default all to 'left' if no pane specified)
  const leftTabs = tabs.filter(t => !t.pane || t.pane === 'left');
  const rightTabs = tabs.filter(t => t.pane === 'right');

  const clearDragOver = () => {
    setDragOverIndex(null);
    setDragOverPosition(null);
    setDragOverPane(null);
  };

  useEffect(() => {
    const clear = () => clearDragOver();
    document.addEventListener('drop', clear, true);
    document.addEventListener('dragend', clear, true);
    return () => {
      try { document.removeEventListener('drop', clear, true); } catch (e) {}
      try { document.removeEventListener('dragend', clear, true); } catch (e) {}
    };
  }, []);

  useEffect(() => {
    if (!tabMenuState) return;
    const stillExists = tabs.some(t => t.id === tabMenuState.tabId);
    if (!stillExists) {
      setTabMenuState(null);
      tabMenuTriggerRef.current = null;
    }
  }, [tabs, tabMenuState]);

  const getPaneForTab = (tab) => (tab?.pane === 'right' ? 'right' : 'left');
  const getPaneTabs = (allTabs, pane) => allTabs.filter(t => getPaneForTab(t) === pane);

  const finalizeTabState = (nextTabs, nextLeftActiveTabId, nextRightActiveTabId, preferredPane = activePane) => {
    const normalizedLeft = nextLeftActiveTabId || null;
    const normalizedRight = nextRightActiveTabId || null;

    const hasLeftTabs = nextTabs.some(t => getPaneForTab(t) === 'left');
    const hasRightTabs = nextTabs.some(t => getPaneForTab(t) === 'right');

    let nextPane = preferredPane;
    if (nextPane === 'right' && !hasRightTabs && hasLeftTabs) nextPane = 'left';
    if (nextPane === 'left' && !hasLeftTabs && hasRightTabs) nextPane = 'right';
    if (splitScreen && !hasRightTabs) {
      setSplitScreen?.(false);
      nextPane = 'left';
    }

    setTabs(nextTabs);
    setLeftActiveTabId?.(normalizedLeft);
    setRightActiveTabId?.(normalizedRight);
    setActivePane?.(nextPane);

    const focusedTabId = nextPane === 'right' ? normalizedRight : normalizedLeft;
    setActiveTabId(focusedTabId || null);
    setSelectedDocId(
      focusedTabId
        ? nextTabs.find(t => t.id === focusedTabId)?.docId || null
        : null
    );
  };

  const moveTabToFirstInPane = (tabId, pane) => {
    const paneTabs = getPaneTabs(tabs, pane);
    const tabIndex = paneTabs.findIndex(t => t.id === tabId);
    if (tabIndex <= 0) return;

    const tabToMove = paneTabs[tabIndex];
    const reorderedPaneTabs = [tabToMove, ...paneTabs.filter(t => t.id !== tabId)];
    const otherPaneTabs = getPaneTabs(tabs, pane === 'left' ? 'right' : 'left');
    const nextTabs = pane === 'left'
      ? [...reorderedPaneTabs, ...otherPaneTabs]
      : [...otherPaneTabs, ...reorderedPaneTabs];

    finalizeTabState(nextTabs, leftActiveTabId, rightActiveTabId, activePane);
  };

  const moveTabToPane = (tabId, targetPane) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;

    const sourcePane = getPaneForTab(tab);
    if (sourcePane === targetPane) return;

    const sourcePaneTabs = getPaneTabs(tabs, sourcePane);
    const sourceIndex = sourcePaneTabs.findIndex(t => t.id === tabId);
    const remainingSourceTabs = sourcePaneTabs.filter(t => t.id !== tabId);
    const targetPaneTabs = getPaneTabs(tabs, targetPane);

    const movedTab = { ...tab, pane: targetPane };
    const nextLeftTabs = targetPane === 'left'
      ? [...targetPaneTabs, movedTab]
      : remainingSourceTabs;
    const nextRightTabs = targetPane === 'right'
      ? [...targetPaneTabs, movedTab]
      : remainingSourceTabs;
    const nextTabs = [...nextLeftTabs, ...nextRightTabs];

    let nextLeftActive = leftActiveTabId;
    let nextRightActive = rightActiveTabId;

    if (targetPane === 'left') nextLeftActive = tabId;
    if (targetPane === 'right') nextRightActive = tabId;

    const sourceActiveTabId = sourcePane === 'left' ? leftActiveTabId : rightActiveTabId;
    if (sourceActiveTabId === tabId) {
      const fallbackTab = remainingSourceTabs[Math.max(0, sourceIndex - 1)] || remainingSourceTabs[0] || null;
      if (sourcePane === 'left') nextLeftActive = fallbackTab?.id || null;
      if (sourcePane === 'right') nextRightActive = fallbackTab?.id || null;
    }

    if (targetPane === 'right') {
      setSplitScreen?.(true);
    }

    finalizeTabState(nextTabs, nextLeftActive, nextRightActive, targetPane);
  };

  const closeTabsByIds = (idsToClose) => {
    const idsSet = new Set(idsToClose);
    if (idsSet.size === 0) return;

    const nextTabs = tabs.filter(t => !idsSet.has(t.id));
    const nextLeftTabs = getPaneTabs(nextTabs, 'left');
    const nextRightTabs = getPaneTabs(nextTabs, 'right');

    const nextLeftActive = nextLeftTabs.some(t => t.id === leftActiveTabId)
      ? leftActiveTabId
      : (nextLeftTabs[0]?.id || null);

    const nextRightActive = nextRightTabs.some(t => t.id === rightActiveTabId)
      ? rightActiveTabId
      : (nextRightTabs[0]?.id || null);

    finalizeTabState(nextTabs, nextLeftActive, nextRightActive, activePane);
  };

  const closeTabsBefore = (tabId, pane) => {
    const paneTabs = getPaneTabs(tabs, pane);
    const index = paneTabs.findIndex(t => t.id === tabId);
    if (index <= 0) return;
    closeTabsByIds(paneTabs.slice(0, index).map(t => t.id));
  };

  const closeTabsAfter = (tabId, pane) => {
    const paneTabs = getPaneTabs(tabs, pane);
    const index = paneTabs.findIndex(t => t.id === tabId);
    if (index < 0 || index >= paneTabs.length - 1) return;
    closeTabsByIds(paneTabs.slice(index + 1).map(t => t.id));
  };

  const closeAllTabs = () => {
    setTabs([]);
    setLeftActiveTabId?.(null);
    setRightActiveTabId?.(null);
    setActiveTabId(null);
    setSelectedDocId(null);
    setActivePane?.('left');
    setSplitScreen?.(false);
  };

  const closeThisTab = (tabId, pane) => {
    const paneTabs = pane === 'left'
      ? tabs.filter(t => !t.pane || t.pane === 'left')
      : tabs.filter(t => t.pane === 'right');

    const paneActiveTabId = pane === 'left' ? leftActiveTabId : rightActiveTabId;
    const paneResult = closeTab(paneTabs, paneActiveTabId, tabId);
    const remainingTabs = tabs.filter(t => t.id !== tabId);
    const remainingRightTabs = remainingTabs.filter(t => t.pane === 'right');

    setTabs(remainingTabs);

    if (pane === 'left') {
      setLeftActiveTabId?.(paneResult.activeTabId);
    } else {
      setRightActiveTabId?.(paneResult.activeTabId);
      if (splitScreen && remainingRightTabs.length === 0) {
        setSplitScreen?.(false);
        setActivePane?.('left');
      }
    }

    const wasFocusedTab = activePane === pane && paneActiveTabId === tabId;
    if (wasFocusedTab) {
      setActiveTabId(paneResult.activeTabId);
      setSelectedDocId(
        paneResult.activeTabId
          ? remainingTabs.find(t => t.id === paneResult.activeTabId)?.docId || null
          : null
      );
    } else if (remainingTabs.length === 0) {
      setActiveTabId(null);
      setSelectedDocId(null);
    }
  };

  const openTabContextMenu = (e, tab, pane) => {
    e.preventDefault();
    e.stopPropagation();

    const triggerEl = tabRefs.current[tab.id] || e.currentTarget;
    tabMenuTriggerRef.current = triggerEl;

    const paneTabs = getPaneTabs(tabs, pane);
    const tabIndex = paneTabs.findIndex(t => t.id === tab.id);
    const menuItems = [
      tabIndex > 0 ? { label: 'Move to first' } : null,
      pane !== 'right' ? { label: 'Move to right editor' } : null,
      pane !== 'left' ? { label: 'Move to left editor' } : null,
      { divider: true },
      tabIndex > 0 ? { label: 'Close tabs before' } : null,
      tabIndex < paneTabs.length - 1 ? { label: 'Close tabs after' } : null,
      { label: 'Close all tabs' },
      { label: 'Close this tab' }
    ].filter(Boolean);

    const placement = screenSafePlacement('bottom right', triggerEl, {
      itemCount: menuItems.length,
      minMenuWidth: 220
    });

    setTabMenuState({ tabId: tab.id, pane, placement });
  };

  const tabMenuItems = (() => {
    if (!tabMenuState) return [];
    const { tabId, pane } = tabMenuState;
    const paneTabs = getPaneTabs(tabs, pane);
    const tabIndex = paneTabs.findIndex(t => t.id === tabId);
    if (tabIndex < 0) return [];

    return [
      tabIndex > 0 && {
        label: 'Move to first',
        iconClass: 'caret-line-left',
        onClick: () => moveTabToFirstInPane(tabId, pane)
      },
      pane !== 'right' && {
        label: 'Move to right editor',
        iconClass: 'square-half',
        onClick: () => moveTabToPane(tabId, 'right')
      },
      pane !== 'left' && {
        label: 'Move to left editor',
        iconClass: 'square-half r180',
        onClick: () => moveTabToPane(tabId, 'left')
      },
      { divider: true },
      tabIndex > 0 && {
        label: 'Close tabs before',
        iconClass: 'arrow-line-left',
        onClick: () => closeTabsBefore(tabId, pane)
      },
      tabIndex < paneTabs.length - 1 && {
        label: 'Close tabs after',
        iconClass: 'arrow-line-right',
        onClick: () => closeTabsAfter(tabId, pane)
      },
      {
        label: 'Close all tabs',
        iconClass: 'x-square',
        onClick: closeAllTabs
      },
      {
        label: 'Close this tab',
        iconClass: 'x',
        onClick: () => closeThisTab(tabId, pane)
      }
    ].filter(Boolean);
  })();

  const computeIndexFromX = (clientX, containerRef) => {
    const container = containerRef.current;
    if (!container) return { index: 0, pos: 'after' };
    const children = Array.from(container.querySelectorAll('.tab'));
    if (children.length === 0) return { index: 0, pos: 'after' };

    for (let i = 0; i < children.length; i++) {
      const el = children[i];
      const rect = el.getBoundingClientRect();
      const mid = rect.left + rect.width / 2;
      if (clientX < mid) {
        return { index: i, pos: 'before' };
      }
    }

    // after last
    return { index: children.length - 1, pos: 'after' };
  };

  const handlePaneDragOver = (e, pane) => {
    e.preventDefault();
    e.stopPropagation();
    const containerRef = pane === 'left' ? tabsLeftRef : tabsRightRef;
    const { index, pos } = computeIndexFromX(e.clientX, containerRef);
    setDragOverIndex(index);
    setDragOverPosition(pos);
    setDragOverPane(pane);
  };

  const handleDoubleClick = async (e, pane) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Viewers cannot create new documents
    if (!isEditable) return;
    if (!currentProject || !user) return;
    
    // Create new doc in root of project
    const newDoc = await createDoc(
      currentProject.id,
      null, // parent_id = null for root
      user.id,
      itemsMap,
      setItemsMap
    );
    
    if (newDoc) {
      const beh = computeOpenBehavior({ type: 'doc', tabs, activePane, leftActiveTabId, rightActiveTabId });
      const targetPane = beh?.preferredPane === 'right' ? 'right' : 'left';
      const paneActiveTabId = targetPane === 'right' ? rightActiveTabId : leftActiveTabId;
      const setPaneActiveTabId = targetPane === 'right' ? setRightActiveTabId : setLeftActiveTabId;
      const setFocusedTabId = (tabId) => {
        setPaneActiveTabId?.(tabId);
        setActiveTabId?.(tabId);
      };
      
      // Open doc in the pane resolved from Navigation settings.
      openDoc(
        newDoc,
        tabs,
        setTabs,
        paneActiveTabId,
        setFocusedTabId,
        null,
        'new'
      );
      
      // Set pane on the newly created tab
      setTimeout(() => {
        setTabs(prev => prev.map(t => 
          t.docId === newDoc.id && !t.pane ? { ...t, pane: targetPane } : t
        ));
      }, 0);
      
      // Focus the resolved pane.
      setActivePane?.(targetPane);
    }
  };

  const handlePaneDrop = (e, pane) => {
    e.preventDefault();
    e.stopPropagation();
    const containerRef = pane === 'left' ? tabsLeftRef : tabsRightRef;
    const { index, pos } = computeIndexFromX(e.clientX, containerRef);
    const container = containerRef.current;
    const children = container ? Array.from(container.querySelectorAll('.tab')) : [];
    const insertAt = children.length === 0 ? 0 : ((pos === 'before') ? index : index + 1);
    
    // Get dragged tab info before the drop (supports typed ids like "tab:abc").
    const draggedRawId = String(draggedId || '').trim();
    const draggedTabId = draggedRawId.includes(':') ? draggedRawId.split(':').slice(1).join(':') : draggedRawId;
    const draggedTab = tabs.find(t => t.id === draggedTabId);
    const wasActive = draggedTab && (
      (draggedTab.pane === 'left' || !draggedTab.pane) ? draggedTab.id === leftActiveTabId : draggedTab.id === rightActiveTabId
    );
    const sourcePane = draggedTab?.pane || 'left';
    const movingBetweenPanes = sourcePane !== pane;
    
    // Delegate drop to centralized handler which returns true when handled
    try {
      const handled = Boolean(dropInTabs?.(e, insertAt, pane));

      if (handled && pane === 'right') {
        setSplitScreen?.(true);
      }

      // Always activate the dropped tab in target pane when tab drop is handled.
      if (handled && draggedTab) {
        if (pane === 'left') {
          setLeftActiveTabId?.(draggedTab.id);
        } else {
          setRightActiveTabId?.(draggedTab.id);
        }
        setActiveTabId?.(draggedTab.id);
        setActivePane?.(pane);

        // If we moved between panes and the tab was active in source, select new active in source
        if (wasActive && movingBetweenPanes) {
          const sourcePaneTabs = sourcePane === 'left' ? leftTabs : rightTabs;
          const remainingTabsInSource = sourcePaneTabs.filter(t => t.id !== draggedTab.id);
          
          if (remainingTabsInSource.length > 0) {
            // Find the tab that was before the dragged one, or use the first remaining
            const draggedIndex = sourcePaneTabs.findIndex(t => t.id === draggedTab.id);
            const newActiveTab = remainingTabsInSource[Math.max(0, draggedIndex - 1)];
            
            if (sourcePane === 'left') {
              setLeftActiveTabId?.(newActiveTab.id);
            } else {
              setRightActiveTabId?.(newActiveTab.id);
            }
          } else {
            // No tabs left in source pane
            if (sourcePane === 'left') {
              setLeftActiveTabId?.(null);
            } else {
              setRightActiveTabId?.(null);
              if (splitScreen && pane === 'left') {
                setSplitScreen?.(false);
                setActivePane?.('left');
              }
            }
          }
        }
      }

      // Non-tab drops (docs/chats/versions) still need pane focus for the dropped tab to appear focused.
      if (handled && !draggedTab) {
        setActivePane?.(pane);
      }
    } catch (err) {
      debug('TabShelf.handlePaneDrop.err', { type: 'error' }, err);
    }

    clearDragOver();
  };

  const handleSplitButtonDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handlePaneDrop(e, 'right');
  };

  const renderTabsForPane = (tabsList, pane) => {
    // Determine which active tab ID to use based on pane
    const paneActiveTabId = pane === 'left' ? leftActiveTabId : rightActiveTabId;
    // A tab is 'focused' if it's active in its pane AND that pane is the active pane
    const isFocused = (tabId) => tabId === paneActiveTabId && activePane === pane;
    
    return tabsList.map((tab, idx) => (
      <Tab
        key={tab.id}
        id={tab.id}
        tabId={tab.id}
        tabRef={el => {
          if (el) tabRefs.current[tab.id] = el;
          else delete tabRefs.current[tab.id];
        }}
        label={tab.label}
        active={tab.id === paneActiveTabId}
        focused={isFocused(tab.id)}
        isVersion={tab.isVersion}
        isChat={tab.docType === 'chat'}
        onContextMenu={e => openTabContextMenu(e, tab, pane)}
        onClick={() => {
          debug('switchTab', { type: 'log' }, `docId=${tab.docId} tabId=${tab.id} pane=${pane} label="${tab.label}"`);
          // Set the pane-specific active tab
          if (pane === 'left') {
            setLeftActiveTabId?.(tab.id);
          } else {
            setRightActiveTabId?.(tab.id);
          }
          // Update active pane
          setActivePane?.(pane);
          // This will trigger the effect in EditorLayout to sync activeTabId
          setSelectedDocId?.(tab.docId);
        }}
        onClose={() => {
          const paneTabs = pane === 'left'
            ? tabs.filter(t => !t.pane || t.pane === 'left')
            : tabs.filter(t => t.pane === 'right');

          const paneResult = closeTab(paneTabs, paneActiveTabId, tab.id);
          const remainingTabs = tabs.filter(t => t.id !== tab.id);
          const remainingRightTabs = remainingTabs.filter(t => t.pane === 'right');

          setTabs(remainingTabs);

          if (pane === 'left') {
            setLeftActiveTabId?.(paneResult.activeTabId);
          } else {
            setRightActiveTabId?.(paneResult.activeTabId);
            if (splitScreen && remainingRightTabs.length === 0) {
              setSplitScreen?.(false);
              setActivePane?.('left');
            }
          }

          const wasFocusedTab = activePane === pane && paneActiveTabId === tab.id;
          if (wasFocusedTab) {
            setActiveTabId(paneResult.activeTabId);
            setSelectedDocId(
              paneResult.activeTabId
                ? remainingTabs.find(t => t.id === paneResult.activeTabId)?.docId || null
                : null
            );
          } else if (remainingTabs.length === 0) {
            setActiveTabId(null);
            setSelectedDocId(null);
          }
        }}
        onLabelChange={async (newLabel) => { 
          if (!itemsMap || !setItemsMap) return; 

          await renameItem(
            tab.docId,       // itemId
            'doc',           // type
            newLabel,        // newName
            itemsMap,        // itemsMap
            setItemsMap,     // setter for project items
            null,
            null,
            currentProject?.id,
            user?.id
          );

          // update tab label separately
          setTabs(prev =>
            prev.map(t =>
              t.id === tab.id
                ? { ...t, label: newLabel }
                : t
            )
          );
        }}
        canRename={isEditable && !tab.isVersion}
        onDragStart={e => { e.stopPropagation(); dragStart?.(e, tab.id, 'tab'); }}
        onDragOver={e => handlePaneDragOver(e, pane)}
        onDrop={e => handlePaneDrop(e, pane)}
        onDragEnd={() => { dragEnd?.(); clearDragOver(); }}
        showInsertBefore={dragOverPane === pane && dragOverIndex === idx && dragOverPosition === 'before'}
        showInsertAfter={dragOverPane === pane && dragOverIndex === idx && dragOverPosition === 'after'}
      />
    ));
  };

  return (
    <div id="tabShelf" className={splitScreen ? 'split' : ''}>
      <div
        id="tabsLeft"
        ref={tabsLeftRef}
        style={splitScreen ? { width: `${leftPercent}%` } : undefined}
        onDragOver={e => handlePaneDragOver(e, 'left')}
        onDrop={e => handlePaneDrop(e, 'left')}
        onDragLeave={clearDragOver}
        onDoubleClick={e => handleDoubleClick(e, 'left')}
      >
        {renderTabsForPane(leftTabs, 'left')}
      </div>

      {splitScreen && (
        <div
          id="tabsRight"
          ref={tabsRightRef}
          style={splitScreen ? { width: `calc(${100 - leftPercent}% - 100px)` } : undefined}
          onDragOver={e => handlePaneDragOver(e, 'right')}
          onDrop={e => handlePaneDrop(e, 'right')}
          onDragLeave={clearDragOver}
          onDoubleClick={e => handleDoubleClick(e, 'right')}
        >
          {renderTabsForPane(rightTabs, 'right')}
        </div>
      )}

      <div id="tabControls">
          <button
            id="splitScreenButton"
            className={`iconButton ${splitScreen ? 'active' : ''}`}
            onClick={() => {
              setSplitScreen?.(prev => {
                const newValue = !prev;
                if (newValue && setActivePane) {
                  setActivePane('right');
                }
                return newValue;
              });
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={handleSplitButtonDrop}
            aria-label="Toggle split screen"
            data-tooltip="Toggle split screen"
            data-tooltip-placement="bottom-left"
          >
            <Icon icon={'square-split-horizontal'} className="icon square-split-horizontal" />
          </button>
          <button
            id="optionsButton"
            className={`iconButton ${optionsOpen ? 'active' : ''}`}
            onClick={onToggleOptions}
            aria-label="Options"
            data-tooltip="Options"
            data-tooltip-placement="bottom-left"
          >
            <Icon icon={'sliders-horizontal'} className="icon sliders-horizontal" />
          </button>
      </div>

      {tabMenuState && tabMenuItems.length > 0 && (
        <ContextMenu
          triggerRef={tabMenuTriggerRef}
          placement={tabMenuState.placement || 'bottom right'}
          items={tabMenuItems}
          onClose={() => {
            setTabMenuState(null);
            tabMenuTriggerRef.current = null;
          }}
        />
      )}
    </div>
  );
}

